import React, { useState } from 'react';
import {
  Box,
  Heading,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Switch,
  HStack,
  Card,
  CardBody,
  useToast,
  Flex,
  Spacer,
  Text,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { EventType, EventCreate } from '../types/api';
import { localDateTimeToUTC } from '../utils/dateUtils';

const CreateEventPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();

  // Form state
  const [formData, setFormData] = useState<EventCreate>({
    title: '',
    description: '',
    short_description: '',
    event_type: 'press_conference' as EventType,
    event_date: '',
    event_end_date: '',
    location: '',
    is_virtual: false,
    meeting_url: '',
    max_attendees: undefined,
    requires_registration: true,
    response_deadline: '',
    allow_late_responses: false,
    allow_guests: false,
    internal_notes: '',
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: EventCreate) => apiService.createEvent(data),
    onSuccess: (data) => {
      toast({
        title: 'Événement créé',
        description: 'L\'événement a été créé avec succès',
        status: 'success',
        duration: 3000,
      });
      navigate(`/events/${data.id}`);
    },
    onError: (error: any) => {
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible de créer l\'événement',
        status: 'error',
        duration: 5000,
      });
    },
  });

  // Update the handleSubmit function (around line 57)
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.title || !formData.event_date) {
      toast({
        title: 'Champs requis',
        description: 'Veuillez remplir tous les champs obligatoires',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    // Convert dates from Paris timezone to UTC before sending
    const dataToSend = {
      ...formData,
      event_date: localDateTimeToUTC(formData.event_date),
      event_end_date: formData.event_end_date ? localDateTimeToUTC(formData.event_end_date) : '',
      response_deadline: formData.response_deadline ? localDateTimeToUTC(formData.response_deadline) : '',
    };

    createMutation.mutate(dataToSend);
  };

  const handleChange = (field: keyof EventCreate, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Box>
      {/* Header */}
      <Flex mb={6} align="center">
        <Heading>Créer un événement</Heading>
        <Spacer />
        <Button variant="outline" onClick={() => navigate('/events')}>
          Annuler
        </Button>
      </Flex>

      <form onSubmit={handleSubmit}>
        <VStack spacing={6} align="stretch">
          {/* Basic Information */}
          <Card>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Heading size="md" mb={2}>Informations générales</Heading>

                <FormControl isRequired>
                  <FormLabel>Titre de l'événement</FormLabel>
                  <Input
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    placeholder="Ex: Conférence de presse - Lancement saison 2025"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description courte</FormLabel>
                  <Input
                    value={formData.short_description}
                    onChange={(e) => handleChange('short_description', e.target.value)}
                    placeholder="Résumé en une phrase"
                    maxLength={500}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description complète</FormLabel>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Description détaillée de l'événement"
                    rows={6}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Type d'événement</FormLabel>
                  <Select
                    value={formData.event_type}
                    onChange={(e) => handleChange('event_type', e.target.value as EventType)}
                  >
                    <option value="press_conference">Conférence de presse</option>
                    <option value="vip_event">Événement VIP</option>
                    <option value="training">Formation</option>
                    <option value="other">Autre</option>
                  </Select>
                </FormControl>
              </VStack>
            </CardBody>
          </Card>

          {/* Date & Time */}
          <Card>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Heading size="md" mb={2}>Date et horaire</Heading>

                <FormControl isRequired>
                  <FormLabel>Date et heure de début</FormLabel>
                  <Input
                    type="datetime-local"
                    value={formData.event_date}
                    onChange={(e) => handleChange('event_date', e.target.value)}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Date et heure de fin</FormLabel>
                  <Input
                    type="datetime-local"
                    value={formData.event_end_date}
                    onChange={(e) => handleChange('event_end_date', e.target.value)}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Date limite de réponse</FormLabel>
                  <Input
                    type="datetime-local"
                    value={formData.response_deadline}
                    onChange={(e) => handleChange('response_deadline', e.target.value)}
                  />
                  <Text fontSize="sm" color="gray.600" mt={1}>
                    Date limite pour que les invités confirment leur présence
                  </Text>
                </FormControl>

                <FormControl>
                  <HStack justify="space-between">
                    <FormLabel mb={0}>Autoriser les réponses tardives</FormLabel>
                    <Switch
                      isChecked={formData.allow_late_responses}
                      onChange={(e) => handleChange('allow_late_responses', e.target.checked)}
                    />
                  </HStack>
                </FormControl>
              </VStack>
            </CardBody>
          </Card>

          {/* Location */}
          <Card>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Heading size="md" mb={2}>Lieu</Heading>

                <FormControl>
                  <HStack justify="space-between">
                    <FormLabel mb={0}>Événement virtuel</FormLabel>
                    <Switch
                      isChecked={formData.is_virtual}
                      onChange={(e) => handleChange('is_virtual', e.target.checked)}
                    />
                  </HStack>
                </FormControl>

                {formData.is_virtual ? (
                  <FormControl>
                    <FormLabel>URL de réunion</FormLabel>
                    <Input
                      type="url"
                      value={formData.meeting_url}
                      onChange={(e) => handleChange('meeting_url', e.target.value)}
                      placeholder="https://zoom.us/j/..."
                    />
                  </FormControl>
                ) : (
                  <>
                    <FormControl>
                      <FormLabel>Lieu</FormLabel>
                      <Input
                        value={formData.location}
                        onChange={(e) => handleChange('location', e.target.value)}
                        placeholder="Ex: Hôtel de Ville, Paris"
                      />
                    </FormControl>

                  </>
                )}
              </VStack>
            </CardBody>
          </Card>

          {/* Capacity & Settings */}
          <Card>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Heading size="md" mb={2}>Capacité et paramètres</Heading>

                <FormControl>
                  <FormLabel>Nombre maximum de participants</FormLabel>
                  <NumberInput
                    value={formData.max_attendees || ''}
                    onChange={(valueString) => handleChange('max_attendees', valueString ? parseInt(valueString) : undefined)}
                    min={1}
                  >
                    <NumberInputField placeholder="Illimité si vide" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </FormControl>

                <FormControl>
                  <HStack justify="space-between">
                    <FormLabel mb={0}>Autoriser les accompagnants (+1)</FormLabel>
                    <Switch
                      isChecked={formData.allow_guests}
                      onChange={(e) => handleChange('allow_guests', e.target.checked)}
                    />
                  </HStack>
                </FormControl>

              </VStack>
            </CardBody>
          </Card>

          {/* Protocol & Notes */}
          <Card>
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Heading size="md" mb={2}>Notes</Heading>

                <FormControl>
                  <FormLabel>Notes internes</FormLabel>
                  <Textarea
                    value={formData.internal_notes}
                    onChange={(e) => handleChange('internal_notes', e.target.value)}
                    placeholder="Notes privées pour l'équipe organisatrice"
                    rows={4}
                  />
                </FormControl>
              </VStack>
            </CardBody>
          </Card>

          {/* Actions */}
          <HStack justify="flex-end" spacing={4}>
            <Button
              variant="outline"
              onClick={() => navigate('/events')}
              isDisabled={createMutation.isPending}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              colorScheme="blue"
              isLoading={createMutation.isPending}
              loadingText="Création..."
            >
              Créer l'événement
            </Button>
          </HStack>
        </VStack>
      </form>
    </Box>
  );
};

export default CreateEventPage;